import {Component, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {GatewayRoleResourceService, RoleDTO} from '@tj/tj-dmp-lib-api';
import {MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin} from 'rxjs';
import {RoleRulesService} from '../../../../services/role-rules.service';

type Access = 'NONE' | 'READ' | 'EDIT' | 'WRITE';

// One feature of the role, with the access level the role gives to it.
interface PermissionRow {
    group: string;
    rule: string;
    description: string;
    // Feature whose access level this one follows: its own level is neither chosen nor saved.
    followedRule?: string;
    access: Access;
}

// Only a name starting with ROLE_ is recognised as a role in access rights: the prefix is fixed, the user types the
// rest (gateway rule: ^ROLE_[A-Z0-9_]+$, 50 characters at most).
const NAME_PREFIX = 'ROLE_';
const NAME_SUFFIX_PATTERN = /^[A-Z0-9_]+$/;
const NAME_MAX_LENGTH = 50;

@Component({
    templateUrl: './role-edit.component.html',
    styleUrls: ['./role-edit.component.scss'],
    standalone: false
})
export class RoleEditComponent implements OnInit {

    readonly accessLevels: { label: string, value: Access }[] = (['NONE', 'READ', 'EDIT', 'WRITE'] as Access[])
        .map(value => ({label: this.translate.instant(`roleEdit.permissions.levels.${value}`), value}));

    breadcrumb: MenuItem[] = [
        {label: this.translate.instant('menu.admin.label')},
        {label: this.translate.instant('menu.admin.roles'), routerLink: '/h/admin/role'},
        {label: this.translate.instant('roleEdit.titleCreate')}
    ];
    readonly namePrefix = NAME_PREFIX;
    actual: RoleDTO;
    form: UntypedFormGroup;
    rows: PermissionRow[] = [];
    saving = false;

    // Names of the existing roles, lower-cased: the name is the role's key, and saving a new role under
    // a taken name would overwrite that role.
    private existingNames: string[] = [];

    constructor(private fb: UntypedFormBuilder,
                private router: Router,
                private messageService: MessageService,
                private route: ActivatedRoute,
                private readonly gatewayRoleResourceService: GatewayRoleResourceService,
                private readonly translate: TranslateService,
                private roleRulesService: RoleRulesService
    ) {
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    ngOnInit() {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            forkJoin([this.gatewayRoleResourceService.getRules(), this.gatewayRoleResourceService.getRole(id)])
                .subscribe(([rules, role]) => {
                    this.actual = role;
                    this.breadcrumb = [...this.breadcrumb.slice(0, 2), {label: role.name}];
                    this.rows = this.buildRows(rules, role);
                    this.form = this.fb.group({
                        description: [role.description ?? '', Validators.maxLength(255)]
                    });
                });
        } else {
            this.gatewayRoleResourceService.getRules().subscribe(rules => {
                this.rows = this.buildRows(rules);
                this.form = this.fb.group({
                    name: ['', this.nameValidator],
                    description: ['', Validators.maxLength(255)]
                });
                this.gatewayRoleResourceService.getRoles().subscribe(roles => {
                    this.existingNames = roles.map(role => role.name.trim().toLowerCase());
                    this.f.name.updateValueAndValidity();
                });
            });
        }
    }

    // Saved rules are "<LEVEL>_<RULE>" (e.g. "READ_BREVO_CONFIG").
    private buildRows(apiRules: string[], role?: RoleDTO): PermissionRow[] {
        const accessByRule: Record<string, Access> = {};
        role?.rules?.forEach(r => {
            const {level, feature} = RoleRulesService.splitRule(r.rule);
            accessByRule[feature] = level as Access;
        });
        return this.roleRulesService.getRules(apiRules).map(template => ({
            group: template.group,
            rule: template.rule,
            description: template.description,
            followedRule: RoleRulesService.FOLLOWED_RULES[template.rule],
            access: accessByRule[template.rule] ?? 'NONE'
        }));
    }

    // The control holds the part after the fixed prefix.
    private nameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        const suffix = (control.value ?? '').trim();
        if (!suffix) {
            return {required: true};
        }
        if (!NAME_SUFFIX_PATTERN.test(suffix) || NAME_PREFIX.length + suffix.length > NAME_MAX_LENGTH) {
            return {pattern: true};
        }
        return this.existingNames.includes((NAME_PREFIX + suffix).toLowerCase()) ? {taken: true} : null;
    };

    // Letters are typed in upper case, as the name requires.
    upperCaseName() {
        const value: string = this.f.name.value ?? '';
        if (value !== value.toUpperCase()) {
            this.f.name.setValue(value.toUpperCase());
        }
    }

    // A field's error is shown under it once the user has changed or left it, not on a pristine form.
    showError(field: string): boolean {
        const control = this.form.get(field);
        return control.invalid && (control.dirty || control.touched);
    }

    // Groups are numbered to keep their order ("1. Paramétrage"): the number is not shown.
    groupLabel(group: string): string {
        return group.replace(/^\d+\.\s*/, '');
    }

    accessOf(row: PermissionRow): Access {
        return row.followedRule ? this.rows.find(r => r.rule === row.followedRule)?.access ?? 'NONE' : row.access;
    }

    submit() {
        if (this.form.invalid || this.saving) {
            this.form.markAllAsTouched();
            return;
        }
        this.saving = true;
        const role = {
            name: this.actual ? this.actual.name : NAME_PREFIX + this.form.value.name.trim(),
            description: this.form.value.description,
            rules: this.rows
                .filter(row => !row.followedRule && row.access !== 'NONE')
                .map(row => `${row.access}_${row.rule}`)
        };
        this.gatewayRoleResourceService.saveRole(role).subscribe({
            next: () => {
                this.messageService.add({
                    key: 'common', severity: 'success', summary: this.translate.instant('roleEdit.toasts.successSummary'),
                    detail: this.translate.instant(this.actual ? 'roleEdit.toasts.saveSuccessDetail' : 'roleEdit.toasts.createSuccessDetail')
                });
                this.router.navigate(['/h/admin/role']);
            },
            error: (e) => {
                this.saving = false;
                // A server error with a title has already been shown by ErrorsInterceptor, which then rethrows
                // a string: only the errors it let through get a toast here.
                if (e instanceof HttpErrorResponse) {
                    this.messageService.add({
                        key: 'common', severity: 'error', summary: this.translate.instant('roleEdit.toasts.errorSummary'),
                        detail: this.translate.instant(this.actual ? 'roleEdit.toasts.saveErrorDetail' : 'roleEdit.toasts.createErrorDetail')
                    });
                }
            }
        });
    }
}
